import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalGradients,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  AvatarStack,
  DawTile,
  InitialsAvatar,
  Mono,
  ProgressTrack,
  ScreenFrame,
  SignalButton,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { projects } from '../fixtures';

/** One row in the sidebar's LIBRARY list. */
const NavItem: React.FC<{
  label: string;
  count?: string;
  countColor?: string;
  active?: boolean;
}> = ({ label, count, countColor = signalText.muted, active = false }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '9px 12px',
      borderRadius: `${signalRadii.md}px`,
      fontSize: '13px',
      fontWeight: active ? 600 : 400,
      color: active ? signalText.primary : signalText.secondary,
      background: active ? 'rgba(255,106,31,0.13)' : 'transparent',
      cursor: 'pointer',
      '&:hover': active
        ? undefined
        : { background: signalSurfaces.fillStrong, color: signalText.primary },
    }}
  >
    {/* The accent bar marks the active item; inactive rows keep a transparent
        one so every label stays on the same optical left edge. */}
    <Box
      sx={{
        width: 5,
        height: 16,
        flex: 'none',
        borderRadius: '3px',
        background: active ? signalColors.accent : 'transparent',
      }}
    />
    {label}
    {count && (
      <Mono
        size={active ? 11 : 10}
        weight={active ? 500 : 600}
        color={countColor}
        sx={{ marginLeft: 'auto' }}
      >
        {count}
      </Mono>
    )}
  </Box>
);

/** Filter chip in the toolbar. */
const FilterChip: React.FC<{ label: string; active?: boolean }> = ({
  label,
  active = false,
}) => (
  <Box
    component="span"
    sx={{
      padding: '7px 12px',
      borderRadius: '999px',
      background: active ? signalSurfaces.fillActive : 'transparent',
      border: `1px solid ${active ? signalBorders.stronger : signalBorders.default}`,
      fontFamily: signalFonts.mono,
      fontWeight: 600,
      fontSize: '11px',
      color: active ? signalText.primary : signalText.muted,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      '&:hover': active ? undefined : { color: signalText.primary },
    }}
  >
    {label}
  </Box>
);

/**
 * 01 — Projects grid.
 *
 * The app shell: fixed sidebar, glass header, and a three-up grid of project
 * cards. Every card carries the same four signals — which DAW, how big/what
 * version, sync state, and who is on it — so the grid can be scanned in one
 * pass without opening anything.
 */
export const Screen01Projects: React.FC = () => (
  <ScreenFrame
    index="01"
    title="Projects grid"
    note="DESKTOP · DARK"
    height={900}
    background={signalSurfaces.canvas}
    frameSx={{ display: 'flex' }}
  >
    <Box
      component="aside"
      sx={{
        width: 224,
        flex: 'none',
        background: signalSurfaces.panel,
        borderRight: `1px solid ${signalBorders.divider}`,
        padding: '20px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '22px',
      }}
    >
      <Box sx={{ padding: '0 6px' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}
          component="div"
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 26,
              height: 26,
              flex: 'none',
              borderRadius: '8px',
              background: signalGradients.logoMark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: signalColors.onAccent,
              fontFamily: signalFonts.display,
              fontWeight: 800,
              fontSize: '13px',
            }}
          >
            S
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: signalFonts.display,
              fontWeight: 700,
              fontSize: '15px',
              letterSpacing: '-0.02em',
            }}
          >
            StudioVault
          </Box>
        </Box>
      </Box>

      <SignalButton size="md" sx={{ padding: '10px 14px', width: '100%' }}>
        ＋ Upload session
      </SignalButton>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <Mono
          size={10}
          weight={600}
          letterSpacing="0.12em"
          color={signalText.faint}
          sx={{ padding: '6px 12px' }}
        >
          LIBRARY
        </Mono>
        <NavItem label="Projects" count="18" active />
        <NavItem label="Shared with me" />
        <NavItem label="Stems" />
        <NavItem label="Boards" />
        <NavItem
          label="Transfers"
          count="3"
          countColor={signalColors.accentBright}
        />
      </Box>

      {/* Vault meter pinned to the bottom of the rail. */}
      <Box
        sx={{
          marginTop: 'auto',
          border: `1px solid ${signalBorders.subtle}`,
          borderRadius: `${signalRadii.xl}px`,
          padding: '14px',
          background: signalSurfaces.fillWeak,
          display: 'flex',
          flexDirection: 'column',
          gap: '9px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Mono size={11} color={signalText.secondary}>
            VAULT
          </Mono>
          <Mono size={11} color={signalText.secondary}>
            14.2 / 20 GB
          </Mono>
        </Box>
        <ProgressTrack value={71} />
        <Box sx={{ fontSize: '11px', color: signalText.muted }}>
          Pro plan · renews Sep 3
        </Box>
      </Box>
    </Box>

    <Box
      component="main"
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="header"
        sx={{
          height: 60,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '0 24px',
          borderBottom: `1px solid ${signalBorders.divider}`,
          background: signalSurfaces.glassHeader,
          backdropFilter: 'blur(24px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            maxWidth: 360,
            background: signalSurfaces.fillStrong,
            border: `1px solid ${signalBorders.medium}`,
            borderRadius: `${signalRadii.md}px`,
            padding: '8px 12px',
          }}
        >
          <Box
            component="span"
            sx={{ color: signalText.muted, fontSize: '13px' }}
          >
            ⌕
          </Box>
          <Box
            component="span"
            sx={{ fontSize: '13px', color: signalText.muted, flex: 1 }}
          >
            Search projects, stems, versions…
          </Box>
          <Mono size={11} color={signalText.faint}>
            ⌘K
          </Mono>
        </Box>

        <Box
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <StatusPill
            status="synced"
            dot
            size={10}
            sx={{ padding: '6px 10px' }}
          >
            DESKTOP AGENT CONNECTED
          </StatusPill>
          <InitialsAvatar
            initials="MK"
            bg={signalColors.accent}
            fg={signalColors.onAccent}
            size={28}
          />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          padding: '26px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '20px',
          }}
        >
          <Box>
            <Box
              component="h2"
              sx={{
                margin: 0,
                fontFamily: signalFonts.display,
                fontWeight: 800,
                fontSize: '34px',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              Projects
            </Box>
            <Box
              component="p"
              sx={{
                margin: '6px 0 0',
                fontSize: '13px',
                color: signalText.secondary,
              }}
            >
              18 sessions · 3 need attention
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <FilterChip label="ALL" active />
            <FilterChip label="NEEDS SYNC" />
            <FilterChip label="SHARED" />
            <Box
              sx={{
                width: '1px',
                height: 20,
                background: signalBorders.medium,
                margin: '0 4px',
              }}
            />
            <Mono size={11}>SORT · MODIFIED</Mono>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          {projects.map((project) => (
            <Box
              key={project.name}
              sx={{
                border: `1px solid ${signalBorders.default}`,
                borderRadius: `${signalRadii['2xl']}px`,
                background: signalSurfaces.cardGradient,
                overflow: 'hidden',
                boxShadow: signalShadows.card,
                transition: 'border-color 120ms ease',
                '&:hover': { borderColor: signalBorders.accentHover },
              }}
            >
              <Box
                sx={{
                  padding: '14px 14px 10px',
                  display: 'flex',
                  gap: '11px',
                  alignItems: 'flex-start',
                }}
              >
                <DawTile src={project.logo} label={project.daw} size={36} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontFamily: signalFonts.display,
                      fontWeight: 700,
                      fontSize: '16px',
                      letterSpacing: '-0.015em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {project.name}
                  </Box>
                  <Mono size={10.5} sx={{ marginTop: '3px', display: 'block' }}>
                    {project.meta}
                  </Mono>
                </Box>
                <StatusPill status={project.status} dot>
                  {project.statusLabel}
                </StatusPill>
              </Box>

              <Box
                sx={{
                  height: 56,
                  padding: '0 14px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Waveform bars={project.wave} height={44} sx={{ flex: 1 }} />
              </Box>

              <Box
                sx={{
                  padding: '11px 14px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: `1px solid ${signalBorders.divider}`,
                }}
              >
                <AvatarStack people={project.people} size={22} overlap={7} />
                <Mono size={10.5}>{project.updated}</Mono>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen01Projects;
